<script lang="ts">
  // The report canvas (see REPORT_SIZES): backdrop, optional photo with a shade, and the
  // template's content. Templates lay out by the xr-portrait / xr-square / xr-landscape class on
  // the frame. A story is a portrait layout on a stage inside a taller frame; the backdrop and
  // photo fill the whole frame.
  import { onMount } from 'svelte'
  import { ensureReportFonts } from '../fonts'
  import { hexToRgb, type ReportContext } from '../context'

  export let ctx: ReportContext
  // The template's own backdrop, under the photo (if any).
  export let base = 'radial-gradient(120% 80% at 20% 0%, #16303a 0%, #0c171c 55%, #070b0d 100%)'
  // Where the photo shade is darkest, so text stays readable over any photo.
  export let shade: 'even' | 'bottom' | 'top' | 'both' = 'even'
  // Light templates: dark text, light shade.
  export let light = false
  export let photoFilter = ''

  onMount(ensureReportFonts)

  $: o = Math.max(0, Math.min(100, ctx.options.overlay)) / 100
  $: c = light ? '246, 244, 239' : '5, 9, 11'
  $: shadeCss = {
    even: `linear-gradient(rgba(${c}, ${o * 0.85}), rgba(${c}, ${o * 0.85}))`,
    bottom: `linear-gradient(180deg, rgba(${c}, ${o * 0.35}) 0%, rgba(${c}, ${o * 0.25}) 35%, rgba(${c}, ${Math.min(1, o * 1.35)}) 72%, rgba(${c}, ${Math.min(1, o * 1.6)}) 100%)`,
    top: `linear-gradient(0deg, rgba(${c}, ${o * 0.35}) 0%, rgba(${c}, ${o * 0.3}) 40%, rgba(${c}, ${Math.min(1, o * 1.5)}) 100%)`,
    both: `linear-gradient(180deg, rgba(${c}, ${Math.min(1, o * 1.4)}) 0%, rgba(${c}, ${o * 0.45}) 30%, rgba(${c}, ${o * 0.45}) 55%, rgba(${c}, ${Math.min(1, o * 1.5)}) 100%)`,
  }[shade]
</script>

<div class="xr-frame xr-{ctx.size}" class:xr-light={light} style="width: {ctx.frame.width}px; height: {ctx.frame.height}px; --accent: {ctx.accent}; --accent-rgb: {hexToRgb(ctx.accent)};">
  <div class="xr-layer" style="background: {base};" />
  {#if ctx.background}
    <img class="xr-layer xr-photo" src={ctx.background} alt="" style={photoFilter ? `filter: ${photoFilter};` : ''} />
    <div class="xr-layer" style="background: {shadeCss};" />
  {/if}
  <div class="xr-stage" style="top: {ctx.frame.top}px; width: {ctx.width}px; height: {ctx.height}px;">
    <slot name="decor" />
    <div class="xr-content">
      <slot />
    </div>
  </div>
</div>

<style>
  .xr-frame {
    position: relative;
    overflow: hidden;
    color: #f4f6f8;
    font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
    font-size: 28px;
    line-height: 1.2;
    text-align: left;
    -webkit-font-smoothing: antialiased;
    --display: 'Barlow Condensed', 'Arial Narrow', sans-serif;
    --muted: rgba(244, 246, 248, 0.68);
    --faint: rgba(244, 246, 248, 0.14);
    --panel: rgba(255, 255, 255, 0.07);
    --good: #3ddc84;
    --bad: #ff5d6c;
  }
  .xr-frame.xr-light {
    color: #11181c;
    --muted: rgba(17, 24, 28, 0.6);
    --faint: rgba(17, 24, 28, 0.12);
    --panel: rgba(17, 24, 28, 0.05);
    --good: #12a150;
    --bad: #d6283b;
  }
  /* The send dialog shows reports inside app pages, whose Bootstrap and theme styles target
     generic class names (.label, .badge, ".tiles .tile", ...). So every class in the report
     templates and parts is prefixed "xr-", and this reverts what host rules set on bare
     elements. Its specificity (0,1,2) beats a single host class but loses to the templates'
     scoped styles (.xr-name.svelte-x, 0,2,0), so templates must not style bare elements. SVG is
     left alone so presentation attributes (fill, stroke) still apply. */
  :global(.xr-frame :is(div, span, h1, h2, h3, p, img, strong, small, em, i):not(svg *)) {
    all: revert;
  }
  .xr-frame :global(*) {
    box-sizing: border-box;
  }
  .xr-frame :global(h1), .xr-frame :global(h2), .xr-frame :global(h3), .xr-frame :global(p) {
    margin: 0;
    padding: 0;
    font-weight: inherit;
    line-height: inherit;
  }
  .xr-layer {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  }
  .xr-stage {
    position: absolute;
    left: 0;
  }
  .xr-photo {
    object-fit: cover;
    max-width: none;
  }
  .xr-content {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    padding: 64px 72px 56px;
  }
  .xr-square .xr-content {
    padding: 54px 64px 48px;
  }
  .xr-landscape .xr-content {
    padding: 60px 76px 54px;
  }
  /* Only Fill (and explicit spacers) give up space; text must never be squashed. */
  .xr-content > :global(*) {
    flex-shrink: 0;
  }
  /* The same in the templates' layout grids: a section (even one that clips, like a line-clamped
     title) never gets less than its content needs. A layout with too much in it then overflows
     visibly instead of silently cutting text off. */
  .xr-content :global(.xr-layout > *) {
    min-height: min-content;
  }
</style>
